import { useMemo } from "react";
import { useUsers, useUser } from "@/features/users/hooks/useUsersQueries";
import { UserResponseDtoRole, PathsApiUsersGetParametersQueryRole } from "@/types/api";
import type { User } from "@/types/user";

function getUserLabel(user: User): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name ? `${name} (${user.email})` : user.email;
}

/** Active SUB_ADMIN users eligible for department sub-admin assignment. */
export function useSubAdminCandidates(assignedSubAdminId?: string) {
  const { data, isLoading } = useUsers({
    role: PathsApiUsersGetParametersQueryRole.SUB_ADMIN,
    isActive: true,
    limit: 100,
    page: 1,
  });

  const { data: assignedUser } = useUser(assignedSubAdminId ?? "");

  const candidates = useMemo(() => {
    const fromList = data?.items ?? [];

    if (
      assignedUser &&
      assignedUser.role === UserResponseDtoRole.SUB_ADMIN &&
      !fromList.some((user) => user.id === assignedUser.id)
    ) {
      return [assignedUser, ...fromList];
    }

    return fromList;
  }, [assignedUser, data?.items]);

  const lookup = useMemo(() => {
    const map: Record<string, string> = {};
    for (const user of candidates) {
      map[user.id] = getUserLabel(user);
    }
    if (
      assignedUser &&
      assignedUser.role === UserResponseDtoRole.SUB_ADMIN &&
      !map[assignedUser.id]
    ) {
      map[assignedUser.id] = getUserLabel(assignedUser);
    }
    return map;
  }, [assignedUser, candidates]);

  return { candidates, lookup, isLoading, getUserLabel };
}
